// src/pages/dashboard/ClientProfile.js
// Route: /dashboard/clients/:clientId
// Real-data client profile reading from practitioners/{uid}/clients/{clientId}
// and bookings collection.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, Calendar, DollarSign,
  Users, CheckCircle, AlertCircle, ClipboardList, X, Clock, Trash2, NotebookPen, MessageSquare, Star, MoreHorizontal, FileText, Sparkles, RefreshCw,
} from 'lucide-react';
import { auth } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { getClientById, updateClient, deleteClient, getClientBookings } from '../../services/client-service';
import SessionNotesEditor from '../../components/session-notes/SessionNotesEditor';
import { getIntakeResponses, resendIntakeEmail } from '../../services/intake-form-service';
import { requestManualReview } from '../../services/review-service';
import { db } from '../../config/firebase-config';
import { sanitizeHtml } from '../../utils/sanitize';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getPrepHistory } from '../../services/session-prep-service';
import PractitionerCancelModal from '../../components/booking/PractitionerCancelModal';
import PractitionerRescheduleModal from '../../components/booking/PractitionerRescheduleModal';

// ---------------------------------------------------------------------------
// Edit Client Modal (module scope)
// ---------------------------------------------------------------------------
const EditClientModal = ({ client, onClose, onSave }) => {
  const [form, setForm] = useState({
    firstName: client.firstName || '',
    lastName: client.lastName || '',
    phone: client.phone || '',
    notes: client.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save changes.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Edit Client</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                name="firstName" value={form.firstName} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                name="lastName" value={form.lastName} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              name="phone" type="tel" value={form.phone} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes" value={form.notes} onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Private notes about this client..."
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Delete Confirm Modal (module scope)
// ---------------------------------------------------------------------------
const DeleteConfirmModal = ({ clientName, onClose, onConfirm, deleting }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="text-lg font-bold text-gray-900 mb-2">Delete {clientName} and all their data?</h2>
      <p className="text-sm text-gray-600 mb-6">
        This will remove their profile, session notes, and intake form responses. Booking records will be kept for your financial records but marked as archived. This cannot be undone.
      </p>
      <div className="flex gap-3">
        <button
          type="button" onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button" onClick={onConfirm} disabled={deleting}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// ReviewRequestModal (module scope)
// ---------------------------------------------------------------------------
const ReviewRequestModal = ({ clientName, serviceName, resend, onClose, onConfirm, sending }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="text-lg font-bold text-gray-900 mb-2">
        {resend ? 'Resend review request?' : 'Send review request?'}
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        {resend ? 'Send another review request to ' : 'Send a review request to '}
        <strong className="text-gray-900">{clientName}</strong>? They'll receive an email asking for feedback about their <strong className="text-gray-900">{serviceName || 'session'}</strong> session.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={sending}
          className="flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: '#1F2937' }}
          onMouseEnter={(e) => { if (!sending) e.currentTarget.style.background = '#374151'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#1F2937'; }}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ClientProfile
// ---------------------------------------------------------------------------
const ClientProfile = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [practitionerId, setPractitionerId] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [intakeResponses, setIntakeResponses] = useState([]);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeLoaded, setIntakeLoaded] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [expandedResponseId, setExpandedResponseId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // booking being cancelled
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // booking being rescheduled
  const [cancelling, setCancelling] = useState(false);
  const [reviewRequestingId, setReviewRequestingId] = useState(null); // bookingId being requested
  const [reviewRequestedIds, setReviewRequestedIds] = useState(new Set()); // already sent this session
  const [reviewsByBookingId, setReviewsByBookingId] = useState({}); // bookingId → review doc
  const [openMenuId, setOpenMenuId] = useState(null); // bookingId with open three-dot menu
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 }); // fixed position for dropdown
  const [reviewConfirmBooking, setReviewConfirmBooking] = useState(null); // booking pending review modal confirm
  const [expandedTranscriptId, setExpandedTranscriptId] = useState(null); // bookingId with transcript open
  const [generatingSummaryId, setGeneratingSummaryId] = useState(null); // bookingId currently generating summary
  const [summaryError, setSummaryError] = useState({}); // bookingId → error message
  const [bookingSummaries, setBookingSummaries] = useState({}); // bookingId → aiSummary (optimistic update)
  const [prepSessions, setPrepSessions] = useState([]);
  const [prepLoading, setPrepLoading] = useState(false);
  const [prepLoaded, setPrepLoaded] = useState(false);

  useEffect(() => {
    if (!auth) { setLoading(false); return; }

    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !done) {
        done = true;
        clearTimeout(timeout);
        setPractitionerId(user.uid);
        try {
          const [clientData, bookingData] = await Promise.all([
            getClientById(user.uid, clientId),
            // bookings fetched after client data resolves — we need email
            getClientById(user.uid, clientId).then(c => c ? getClientBookings(user.uid, c.email) : []),
          ]);
          if (!clientData) {
            setNotFound(true);
          } else {
            setClient(clientData);
            setBookings(bookingData);
          }
        } catch (err) {
          console.error('ClientProfile: error loading data:', err);
          setNotFound(true);
        }
        setLoading(false);
      }
    });

    return () => { done = true; clearTimeout(timeout); unsubscribe(); };
  }, [clientId]);

  const toDate = (val) => {
    if (!val) return null;
    if (val.toDate) return val.toDate();
    return new Date(val);
  };

  const formatDate = (val) => {
    const date = toDate(val);
    if (!date) return '—';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatShortDate = (val) => {
    const date = toDate(val);
    if (!date) return '—';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInitials = (name) => (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const handleEditSave = async (updates) => {
    await updateClient(practitionerId, clientId, updates);
    const updated = await getClientById(practitionerId, clientId);
    setClient(updated);
  };

  const handleNoteSaved = (bookingId, notesData) => {
    setBookings(prev => prev.map(b =>
      b.id === bookingId
        ? { ...b, sessionNotes: { ...notesData, updatedAt: new Date() } }
        : b
    ));
  };

  const handleGenerateSummary = async (bookingId) => {
    setGeneratingSummaryId(bookingId);
    setSummaryError(prev => ({ ...prev, [bookingId]: null }));
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const generateSessionSummary = httpsCallable(functions, 'generateSessionSummary');
      const result = await generateSessionSummary({ bookingId });
      const summary = result.data?.summary;
      if (summary) {
        setBookingSummaries(prev => ({ ...prev, [bookingId]: summary }));
        // Also update bookings array for AI badge indicator
        setBookings(prev => prev.map(b =>
          b.id === bookingId ? { ...b, aiSummary: summary } : b
        ));
      }
    } catch (err) {
      setSummaryError(prev => ({
        ...prev,
        [bookingId]: err.message || 'Failed to generate summary. Please try again.',
      }));
    } finally {
      setGeneratingSummaryId(null);
    }
  };

  const handleCancelBooking = async ({ reason, practitionerMessage } = {}) => {
    if (!cancelTarget || !practitionerId) return;
    setCancelling(true);
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const cancelBookingFn = httpsCallable(functions, 'cancelBooking');
      await cancelBookingFn({
        bookingId: cancelTarget.id,
        practitionerId,
        cancelledBy: 'practitioner',
        reason: reason || null,
        practitionerMessage: practitionerMessage || null,
      });
      setBookings(prev => prev.map(b =>
        b.id === cancelTarget.id ? { ...b, status: 'cancelled' } : b
      ));
      setCancelTarget(null);
    } catch (err) {
      console.error('handleCancelBooking error:', err);
      alert(err.message || 'Failed to cancel booking. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleRequestReview = async (bookingId) => {
    if (reviewRequestingId) return;
    setReviewConfirmBooking(null);
    setReviewRequestingId(bookingId);
    try {
      await requestManualReview(bookingId);
      setReviewRequestedIds(prev => new Set([...prev, bookingId]));
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? { ...b, reviewRequestSent: true } : b
      ));
    } catch (err) {
      alert(err.message || 'Failed to send review request. Please try again.');
    } finally {
      setReviewRequestingId(null);
    }
  };

  // Close three-dot menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openMenuId]);

  // Load review status for bookings that have had a review request sent
  useEffect(() => {
    if (!bookings.length || !db) return;
    const sentIds = bookings.filter(b => b.reviewRequestSent).map(b => b.id);
    if (!sentIds.length) return;
    // Firestore 'in' supports up to 30 values; split if needed
    const chunks = [];
    for (let i = 0; i < sentIds.length; i += 30) chunks.push(sentIds.slice(i, i + 30));
    Promise.all(
      chunks.map(chunk =>
        getDocs(query(collection(db, 'reviews'), where('bookingId', 'in', chunk)))
      )
    ).then(snaps => {
      const map = {};
      snaps.forEach(snap => snap.docs.forEach(d => { map[d.data().bookingId] = { id: d.id, ...d.data() }; }));
      setReviewsByBookingId(map);
    }).catch(err => console.error('reviewsByBookingId fetch error:', err.message));
  }, [bookings]);

  // Lazy-load prep sessions when the prep tab is first activated
  useEffect(() => {
    if (activeTab !== 'prep' || prepLoaded || !practitionerId || !clientId) return;
    setPrepLoading(true);
    getPrepHistory(practitionerId, clientId)
      .then(data => { setPrepSessions(data); setPrepLoaded(true); setPrepLoading(false); })
      .catch(() => { setPrepLoaded(true); setPrepLoading(false); });
  }, [activeTab, prepLoaded, practitionerId, clientId]);

  // Lazy-load intake responses when the intake tab is first activated
  useEffect(() => {
    if (activeTab !== 'intake' || intakeLoaded || !practitionerId || !client?.email) return;
    setIntakeLoading(true);
    getIntakeResponses(practitionerId, client.email)
      .then(data => {
        setIntakeResponses(data);
        setIntakeLoaded(true);
        setIntakeLoading(false);
      })
      .catch(() => {
        setIntakeLoaded(true);
        setIntakeLoading(false);
      });
  }, [activeTab, intakeLoaded, practitionerId, client]);

  const handleResendIntake = async (responseId) => {
    setResendingId(responseId);
    try {
      await resendIntakeEmail(practitionerId, responseId);
      // Update sentAt locally (UI feedback)
      setIntakeResponses(prev => prev.map(r =>
        r.id === responseId ? { ...r, sentAt: { toDate: () => new Date() } } : r
      ));
    } catch (err) {
      console.error('Resend failed:', err);
    }
    setResendingId(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClient(practitionerId, clientId);
      navigate('/dashboard/clients');
    } catch (err) {
      console.error('ClientProfile: delete failed:', err);
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-48" />
            <div className="bg-white rounded-xl border border-gray-200 h-40" />
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-lg border border-gray-200 h-24" />)}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 h-64" />
          </div>
        </div>
      </div>
    );
  }

  // 404 state
  if (notFound || !client) {
    return (
      <div className="min-h-screen bg-gray-50 py-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Client not found</h1>
          <p className="text-sm text-gray-600 mb-4">This client record may have been deleted or the link is invalid.</p>
          <button
            onClick={() => navigate('/dashboard/clients')}
            className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium mx-auto transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const clientNotes = [...bookings]
    .filter(b => b.sessionNotes)
    .sort((a, b) => (toDate(b.dateTime)?.getTime() || 0) - (toDate(a.dateTime)?.getTime() || 0));

  const tabs = [
    { id: 'bookings', label: 'Bookings' },
    { id: 'notes', label: clientNotes.length > 0 ? `Notes (${clientNotes.length})` : 'Notes' },
    { id: 'intake', label: 'Intake Forms' },
    { id: 'prep', label: '✨ Prep Notes' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <span
            onClick={() => navigate('/dashboard/practitioner')}
            className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
          >
            Dashboard
          </span>
          <span className="text-gray-400">/</span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/clients')}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/clients'); }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#6D28D9'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#7c3aed'}
            style={{ color: '#7c3aed', fontWeight: 500, cursor: 'pointer', userSelect: 'none', transition: 'color 0.15s ease' }}
          >
            Clients
          </span>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium" aria-current="page">{client.displayName}</span>
        </nav>

        {/* Back button */}
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate('/dashboard/clients')}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/clients'); }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6B7280', cursor: 'pointer', userSelect: 'none', transition: 'color 0.15s ease', marginBottom: '24px' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Clients
        </span>

        {/* Client Header Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)', backgroundSize: '400% 400%', animation: 'auroraShift 16s ease infinite' }}
              >
                {getInitials(client.displayName)}
              </div>

              {/* Info */}
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">{client.displayName}</h1>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    client.status === 'active'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {client.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">{client.email}</span>
                  </div>
                  {client.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">{client.phone}</span>
                    </div>
                  )}
                  {client.firstSessionDate && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">Client since {formatDate(client.firstSessionDate)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Header actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate(`/dashboard/prep/${clientId}`)}
                className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Prep for Session
              </button>
              <button
                onClick={() => navigate(`/messages/compose?to=${encodeURIComponent(client.email)}&name=${encodeURIComponent(client.displayName || '')}`)}
                className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                Message
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 border border-gray-200 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
                aria-label="Delete client"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notes preview */}
          {client.notes && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-sm text-gray-700">{client.notes}</p>
            </div>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Sessions</p>
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{client.totalSessions || 0}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <DollarSign className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">${(client.totalRevenue || 0).toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Member Since</p>
              <Calendar className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-lg font-bold text-gray-900">
              {client.firstSessionDate
                ? toDate(client.firstSessionDate)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                : '—'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-purple-600 border-b-2 border-purple-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <div>
                {bookings.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Calendar className="w-7 h-7 text-gray-400" />
                    </div>
                    <p className="font-medium text-gray-900 mb-1">No bookings yet</p>
                    <p className="text-sm text-gray-500">Bookings will appear here once this client schedules a session.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bookings.map(booking => {
                      const bDate = toDate(booking.dateTime);
                      const isExpanded = expandedBookingId === booking.id;
                      const statusColors = {
                        confirmed: 'bg-emerald-100 text-emerald-700',
                        completed: 'bg-blue-100 text-blue-700',
                        cancelled: 'bg-rose-100 text-rose-700',
                        pending: 'bg-amber-100 text-amber-700',
                        reschedule_proposed: 'bg-amber-100 text-amber-700',
                      };
                      const statusLabels = {
                        confirmed: 'confirmed',
                        completed: 'completed',
                        cancelled: 'cancelled',
                        pending: 'pending',
                        reschedule_proposed: 'reschedule proposed',
                      };
                      return (
                        <div key={booking.id} className="border border-gray-200 rounded-xl overflow-hidden">
                          {/* Card header */}
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  booking.status === 'cancelled' ? 'bg-rose-100' : 'bg-purple-100'
                                }`}>
                                  {booking.status === 'cancelled'
                                    ? <AlertCircle className="w-5 h-5 text-rose-600" />
                                    : <CheckCircle className="w-5 h-5 text-purple-600" />
                                  }
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{booking.service?.name || 'Session'}</p>
                                  <p className="text-sm text-gray-500">
                                    {bDate ? formatShortDate(bDate) : '—'}
                                    {booking.service?.duration ? ` · ${booking.service.duration} min` : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {booking.sessionNotes && (
                                  <NotebookPen className="w-4 h-4 text-purple-400" />
                                )}
                                {booking.transcript?.availableAt && (
                                  <FileText className="w-4 h-4 text-blue-400" title="Transcript available" />
                                )}
                                {(booking.aiSummary || bookingSummaries[booking.id]) && (
                                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#7C3AED', background: '#F5F3FF', borderRadius: '10px', padding: '1px 7px' }}>✨ AI Summary</span>
                                )}
                                {booking.service?.price != null && (
                                  <p className="font-semibold text-gray-900">${booking.service.price}</p>
                                )}
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[booking.status] || 'bg-gray-100 text-gray-600'}`}>
                                  {statusLabels[booking.status] || booking.status || 'confirmed'}
                                </span>
                                {/* Three-dot menu button */}
                                <span
                                  role="button"
                                  tabIndex={0}
                                  aria-label="Booking actions"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (openMenuId === booking.id) { setOpenMenuId(null); return; }
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                    setOpenMenuId(booking.id);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      if (openMenuId === booking.id) { setOpenMenuId(null); return; }
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                      setOpenMenuId(booking.id);
                                    }
                                  }}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', color: '#9CA3AF', transition: 'background 0.15s ease, color 0.15s ease' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#374151'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9CA3AF'; }}
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </span>
                              </div>
                            </div>
                            {booking.client?.message && (
                              <p className="mt-3 text-sm text-gray-600 border-t border-gray-100 pt-3">
                                <span className="font-medium text-gray-700">Client note: </span>
                                {booking.client.message}
                              </p>
                            )}
                          </div>

                          {/* Session Notes section (opened from three-dot menu) */}
                          {isExpanded && (
                            <div className="border-t border-gray-100">
                              <div className="p-4 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-sm font-semibold" style={{ color: '#7c3aed' }}>Session Notes</span>
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setExpandedBookingId(null)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setExpandedBookingId(null); }}
                                    aria-label="Close notes"
                                    style={{ color: '#9CA3AF', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'color 0.15s ease' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#4B5563'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
                                  >
                                    <X className="w-4 h-4" />
                                  </span>
                                </div>
                                <SessionNotesEditor
                                  bookingId={booking.id}
                                  existingNotes={booking.sessionNotes}
                                  onSave={(data) => handleNoteSaved(booking.id, data)}
                                />
                              </div>
                            </div>
                          )}

                          {/* Transcript section — shown when transcript is available */}
                          {expandedTranscriptId === booking.id && booking.transcript?.availableAt && (
                            <div className="border-t border-gray-100">
                              <div className="p-4 bg-blue-50">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-500" />
                                    <span className="text-sm font-semibold text-blue-700">Session Transcript</span>
                                    {booking.transcript.bytes && (
                                      <span className="text-xs text-blue-400">
                                        ({Math.round(booking.transcript.bytes / 1024)}KB)
                                      </span>
                                    )}
                                  </div>
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setExpandedTranscriptId(null)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setExpandedTranscriptId(null); }}
                                    aria-label="Close transcript"
                                    style={{ color: '#9CA3AF', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'color 0.15s ease' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#4B5563'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
                                  >
                                    <X className="w-4 h-4" />
                                  </span>
                                </div>
                                {booking.transcript.text ? (
                                  <pre
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                      fontSize: '12px',
                                      lineHeight: '1.6',
                                      color: '#1F2937',
                                      background: '#fff',
                                      border: '1px solid #BFDBFE',
                                      borderRadius: '8px',
                                      padding: '12px',
                                      maxHeight: '320px',
                                      overflowY: 'auto',
                                      margin: 0,
                                    }}
                                  >
                                    {booking.transcript.text}
                                  </pre>
                                ) : booking.transcript.storagePath ? (
                                  <p className="text-sm text-blue-600">
                                    Transcript stored in cloud storage. Download will be supported in a future update.
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-500">Transcript content unavailable.</p>
                                )}

                                {/* Generate AI Summary button — shown inside transcript panel */}
                                {(() => {
                                  const activeSummary = bookingSummaries[booking.id] || booking.aiSummary;
                                  const isGenerating = generatingSummaryId === booking.id;
                                  const errMsg = summaryError[booking.id];
                                  return (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #BFDBFE' }}>
                                      {!activeSummary && !isGenerating && (
                                        <button
                                          type="button"
                                          onClick={() => handleGenerateSummary(booking.id)}
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 16px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease' }}
                                          onMouseEnter={(e) => { e.currentTarget.style.background = '#6D28D9'; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.background = '#7C3AED'; }}
                                        >
                                          ✨ Generate AI Summary
                                        </button>
                                      )}
                                      {isGenerating && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <button
                                            type="button"
                                            disabled
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 16px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, opacity: 0.7, cursor: 'not-allowed' }}
                                          >
                                            <span className="animate-spin" style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%' }} />
                                            Analyzing session...
                                          </button>
                                          <span style={{ fontSize: '12px', color: '#6B7280' }}>This usually takes 15–30 seconds</span>
                                        </div>
                                      )}
                                      {activeSummary && (
                                        <button
                                          type="button"
                                          onClick={() => handleGenerateSummary(booking.id)}
                                          style={{ fontSize: '12px', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                        >
                                          Regenerate summary
                                        </button>
                                      )}
                                      {errMsg && (
                                        <p style={{ marginTop: '8px', fontSize: '13px', color: '#DC2626' }}>{errMsg}</p>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}

                          {/* AI Summary section — shown when a summary has been generated */}
                          {(() => {
                            const activeSummary = bookingSummaries[booking.id] || booking.aiSummary;
                            if (!activeSummary) return null;
                            const generatedAt = activeSummary.generatedAt?.toDate
                              ? activeSummary.generatedAt.toDate()
                              : activeSummary.generatedAt
                                ? new Date(activeSummary.generatedAt)
                                : null;
                            const timeAgo = generatedAt
                              ? (() => {
                                  const diff = Date.now() - generatedAt.getTime();
                                  if (diff < 60000) return 'just now';
                                  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                                  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                                  return generatedAt.toLocaleDateString();
                                })()
                              : null;
                            return (
                              <div className="border-t border-gray-100">
                                <div style={{ padding: '16px', background: '#FAFAFE' }}>
                                  {/* Header */}
                                  <div className="flex items-center justify-between mb-4">
                                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#5B21B6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      ✨ AI Session Summary
                                    </span>
                                    {timeAgo && (
                                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Generated {timeAgo}</span>
                                    )}
                                  </div>

                                  {activeSummary.parseError ? (
                                    /* Parse error — show raw text */
                                    <div>
                                      <p style={{ fontSize: '12px', color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px' }}>
                                        Summary format couldn't be structured — showing raw AI analysis
                                      </p>
                                      <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                        {activeSummary.rawText}
                                      </p>
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                      {/* Session Overview */}
                                      {activeSummary.sessionOverview && (
                                        <div>
                                          <p style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Session Overview</p>
                                          <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', margin: 0 }}>{activeSummary.sessionOverview}</p>
                                        </div>
                                      )}

                                      {/* Key Themes */}
                                      {activeSummary.keyThemes?.length > 0 && (
                                        <div>
                                          <p style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Key Themes</p>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {activeSummary.keyThemes.map((theme, i) => (
                                              <span key={i} style={{ background: '#F5F3FF', color: '#7C3AED', fontSize: '12px', padding: '2px 8px', borderRadius: '12px', fontWeight: 500 }}>
                                                {theme}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Client Insights */}
                                      {activeSummary.clientInsights && (
                                        <div>
                                          <p style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Client Insights</p>
                                          <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', margin: 0 }}>{activeSummary.clientInsights}</p>
                                        </div>
                                      )}

                                      {/* Action Items */}
                                      {activeSummary.actionItems?.length > 0 && (
                                        <div>
                                          <p style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Action Items</p>
                                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {activeSummary.actionItems.map((item, i) => (
                                              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
                                                <span style={{ color: '#7C3AED', flexShrink: 0, marginTop: '1px' }}>✓</span>
                                                {item}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}

                                      {/* Follow-up Notes */}
                                      {activeSummary.followUpNotes && (
                                        <div style={{ background: '#F5F3FF', borderRadius: '8px', padding: '12px' }}>
                                          <p style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Follow-up Notes</p>
                                          <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', margin: 0 }}>{activeSummary.followUpNotes}</p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div>
                {clientNotes.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <NotebookPen className="w-7 h-7 text-gray-400" />
                    </div>
                    <p className="font-medium text-gray-900 mb-1">No session notes yet</p>
                    <p className="text-sm text-gray-500 max-w-xs mx-auto">
                      Add notes to sessions in the Bookings tab to track observations and outcomes.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {clientNotes.map(booking => {
                      const bDate = toDate(booking.dateTime);
                      const notes = booking.sessionNotes;
                      return (
                        <div key={booking.id} className="border border-gray-200 rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                              <p className="font-medium text-gray-900">{booking.service?.name || 'Session'}</p>
                              <p className="text-sm text-gray-500">{bDate ? formatShortDate(bDate) : '—'}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => { setActiveTab('bookings'); setExpandedBookingId(booking.id); }}
                              className="text-xs font-medium transition-colors"
                              style={{ color: '#7c3aed' }}
                              onMouseEnter={e => e.currentTarget.style.color = '#6D28D9'}
                              onMouseLeave={e => e.currentTarget.style.color = '#7c3aed'}
                            >
                              Edit
                            </button>
                          </div>
                          {notes.themes?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                              {notes.themes.map(t => (
                                <span key={t} style={{
                                  padding: '2px 8px', background: '#F5F3FF', color: '#7C3AED',
                                  borderRadius: '12px', fontSize: '12px', fontWeight: 500,
                                }}>{t}</span>
                              ))}
                            </div>
                          )}
                          <div
                            className="tiptap"
                            style={{ background: 'transparent', minHeight: 'unset', padding: 0, fontSize: '14px' }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(notes.content) }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Prep Notes Tab */}
            {activeTab === 'prep' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div />
                  <button
                    onClick={() => navigate(`/dashboard/prep/${clientId}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', background: '#7C3AED', color: '#fff',
                      borderRadius: '10px', border: 'none', fontSize: '13px',
                      fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#6D28D9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#7C3AED'; }}
                  >
                    <Sparkles style={{ width: '13px', height: '13px' }} />
                    New Prep Session
                  </button>
                </div>
                {prepLoading ? (
                  <div className="space-y-3">
                    {[1, 2].map(i => (
                      <div key={i} className="border border-gray-200 rounded-xl p-4 animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-48 mb-2" />
                        <div className="h-3 bg-gray-100 rounded w-80" />
                      </div>
                    ))}
                  </div>
                ) : prepSessions.length === 0 ? (
                  <div className="text-center py-10">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                      style={{ background: '#F5F3FF' }}
                    >
                      <Sparkles className="w-7 h-7" style={{ color: '#7C3AED' }} />
                    </div>
                    <p className="font-medium text-gray-900 mb-1">No prep sessions yet</p>
                    <p className="text-sm text-gray-500 max-w-xs mx-auto">
                      Use ✨ Prep for Session before your next appointment to get an AI briefing on this client.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {prepSessions.map(prep => {
                      const prepDate = prep.createdAt?.toDate ? prep.createdAt.toDate() : (prep.createdAt ? new Date(prep.createdAt) : null);
                      return (
                        <div key={prep.id} className="border border-gray-200 rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <p className="font-medium text-gray-900" style={{ fontSize: '14px' }}>
                                  {prepDate ? prepDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Prep session'}
                                </p>
                                <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                                  {prep.exchangeCount || 0} exchange{(prep.exchangeCount || 0) !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {prep.summary ? (
                                <p className="text-sm text-gray-600">{prep.summary}</p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">No summary available</p>
                              )}
                            </div>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => navigate(`/dashboard/prep/${clientId}?session=${prep.id}`)}
                              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/dashboard/prep/${clientId}?session=${prep.id}`); }}
                              style={{ fontSize: '13px', fontWeight: 500, color: '#7c3aed', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.15s ease' }}
                              onMouseEnter={e => e.currentTarget.style.color = '#6D28D9'}
                              onMouseLeave={e => e.currentTarget.style.color = '#7c3aed'}
                            >
                              View full prep →
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Intake Forms Tab */}
            {activeTab === 'intake' && (
              <div>
                {intakeLoading ? (
                  <div className="space-y-3">
                    {[1, 2].map(i => (
                      <div key={i} className="border border-gray-200 rounded-xl p-4 animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-48 mb-2" />
                        <div className="h-3 bg-gray-100 rounded w-32" />
                      </div>
                    ))}
                  </div>
                ) : intakeResponses.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ClipboardList className="w-7 h-7 text-gray-400" />
                    </div>
                    <p className="font-medium text-gray-900 mb-1">No intake form responses yet</p>
                    <p className="text-sm text-gray-500 max-w-xs mx-auto mb-4">
                      No intake form responses from {client?.firstName || 'this client'} yet. When they complete a form, their answers will appear here.
                    </p>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate('/dashboard/intake-forms')}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/intake-forms'); }}
                      style={{ fontSize: '14px', fontWeight: 500, color: '#7c3aed', cursor: 'pointer', transition: 'color 0.15s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#6D28D9'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
                    >
                      Manage intake forms →
                    </span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {intakeResponses.map(response => {
                      const isCompleted = response.status === 'completed';
                      const isExpanded = expandedResponseId === response.id;
                      const toDate = (val) => {
                        if (!val) return null;
                        if (val.toDate) return val.toDate();
                        if (val._seconds) return new Date(val._seconds * 1000);
                        return new Date(val);
                      };
                      // eslint-disable-next-line no-unused-vars
                      const _sentDate = toDate(response.sentAt);
                      const completedDate = toDate(response.completedAt);
                      const deadlineDate = toDate(response.deadline);

                      return (
                        <div key={response.id} className="border border-gray-200 rounded-xl overflow-hidden">
                          {/* Card header */}
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium text-gray-900">{response.formTitle}</p>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    isCompleted
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {isCompleted ? 'Completed' : 'Pending'}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500">
                                  {response.serviceName && `${response.serviceName} · `}
                                  {isCompleted
                                    ? completedDate ? `Submitted ${formatShortDate(completedDate)}` : 'Submitted'
                                    : deadlineDate ? `Due by ${formatShortDate(deadlineDate)}` : 'Pending'
                                  }
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {isCompleted && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setExpandedResponseId(isExpanded ? null : response.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setExpandedResponseId(isExpanded ? null : response.id); }}
                                    style={{ fontSize: '14px', fontWeight: 500, color: '#7c3aed', cursor: 'pointer', userSelect: 'none', transition: 'color 0.15s ease' }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#6D28D9'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#7c3aed'}
                                  >
                                    {isExpanded ? 'Hide' : 'View responses'}
                                  </span>
                                )}
                                {!isCompleted && (
                                  <button
                                    type="button"
                                    onClick={() => handleResendIntake(response.id)}
                                    disabled={resendingId === response.id}
                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                  >
                                    {resendingId === response.id ? 'Sending…' : 'Resend'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expanded responses */}
                          {isExpanded && isCompleted && (
                            <div className="border-t border-gray-100 bg-gray-50 p-4">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Responses</p>
                              <div className="space-y-4">
                                {(response.responses || []).map((r, i) => (
                                  <div key={i}>
                                    <p className="text-sm font-medium text-gray-700 mb-1">{r.questionLabel}</p>
                                    <p className="text-sm text-gray-900">
                                      {Array.isArray(r.answer) ? r.answer.join(', ') : (r.answer || '—')}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditClientModal
          client={client}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditSave}
        />
      )}

      {/* Delete Confirm Modal */}
      {showDeleteModal && (
        <DeleteConfirmModal
          clientName={client.displayName}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

      {/* Cancel Booking Modal */}
      {/* Three-dot dropdown — rendered at root with position:fixed to escape card overflow */}
      {openMenuId && (() => {
        const booking = bookings.find(b => b.id === openMenuId);
        if (!booking) return null;
        const bDate = toDate(booking.dateTime);
        const review = reviewsByBookingId[booking.id];
        const requestSent = booking.reviewRequestSent || reviewRequestedIds.has(booking.id);
        const reviewReceived = review?.status === 'published' && review?.rating;
        const isFutureConfirmed = booking.status === 'confirmed' && bDate && bDate > new Date();
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)', minWidth: '196px', zIndex: 70, overflow: 'hidden' }}
          >
            {isFutureConfirmed && (
              <button
                type="button"
                onClick={() => { setOpenMenuId(null); navigate(`/dashboard/prep/${clientId}`); }}
                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-purple-50"
                style={{ color: '#7C3AED', fontWeight: 500 }}
              >
                <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                Prep for session
              </button>
            )}
            <button
              type="button"
              onClick={() => { setOpenMenuId(null); setExpandedBookingId(booking.id); }}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50${isFutureConfirmed ? ' border-t border-gray-100' : ''}`}
              style={{ color: '#7C3AED', fontWeight: 500 }}
            >
              <NotebookPen className="w-3.5 h-3.5 flex-shrink-0" />
              {booking.sessionNotes ? 'Edit session notes' : 'Add session notes'}
            </button>
            {booking.transcript?.availableAt && (
              <button
                type="button"
                onClick={() => { setOpenMenuId(null); setExpandedTranscriptId(expandedTranscriptId === booking.id ? null : booking.id); }}
                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 border-t border-gray-100 hover:bg-blue-50"
                style={{ color: '#2563EB', fontWeight: 500 }}
              >
                <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                View transcript
              </button>
            )}
            {booking.status !== 'cancelled' && (
              reviewReceived ? (
                <div className="w-full px-4 py-2.5 text-sm flex items-center gap-2.5 border-t border-gray-100" style={{ color: '#D4AF5A' }}>
                  <Star className="w-3.5 h-3.5 flex-shrink-0" fill="#D4AF5A" />
                  Review Received ★{Number(review.rating).toFixed(1)}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setOpenMenuId(null); setReviewConfirmBooking(booking); }}
                  className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 border-t border-gray-100 hover:bg-gray-50"
                  style={{ color: '#6B7280', fontWeight: 500 }}
                >
                  <Star className="w-3.5 h-3.5 flex-shrink-0" />
                  {requestSent ? 'Resend review request' : 'Request review'}
                </button>
              )
            )}
            {booking.status === 'confirmed' && (
              <button
                type="button"
                onClick={() => { setOpenMenuId(null); setRescheduleTarget({ ...booking, _dateStr: bDate ? formatShortDate(bDate) : '' }); }}
                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 border-t border-gray-100 hover:bg-purple-50"
                style={{ color: '#7c3aed', fontWeight: 500 }}
              >
                <RefreshCw className="w-3.5 h-3.5 flex-shrink-0" />
                Reschedule
              </button>
            )}
            {booking.status === 'confirmed' && (
              <button
                type="button"
                onClick={() => { setOpenMenuId(null); setCancelTarget({ ...booking, _dateStr: bDate ? formatShortDate(bDate) : '' }); }}
                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 border-t border-gray-100 hover:bg-rose-50"
                style={{ color: '#EF4444', fontWeight: 500 }}
              >
                <X className="w-3.5 h-3.5 flex-shrink-0" />
                Cancel booking
              </button>
            )}
          </div>
        );
      })()}
      {cancelTarget && (
        <PractitionerCancelModal
          booking={cancelTarget}
          clientName={client?.displayName || `${client?.firstName || ''} ${client?.lastName || ''}`.trim()}
          onClose={() => { if (!cancelling) setCancelTarget(null); }}
          onConfirm={handleCancelBooking}
          cancelling={cancelling}
        />
      )}
      {rescheduleTarget && (
        <PractitionerRescheduleModal
          booking={rescheduleTarget}
          clientName={client?.displayName || `${client?.firstName || ''} ${client?.lastName || ''}`.trim()}
          onClose={() => setRescheduleTarget(null)}
          onSuccess={() => {
            // Update the booking status locally
            setBookings(prev => prev.map(b =>
              b.id === rescheduleTarget.id ? { ...b, status: 'reschedule_proposed' } : b
            ));
            setRescheduleTarget(null);
          }}
        />
      )}
      {reviewConfirmBooking && (
        <ReviewRequestModal
          clientName={client?.displayName || `${client?.firstName || ''} ${client?.lastName || ''}`.trim()}
          serviceName={reviewConfirmBooking.service?.name || 'Session'}
          resend={reviewConfirmBooking.reviewRequestSent || reviewRequestedIds.has(reviewConfirmBooking.id)}
          onClose={() => { if (!reviewRequestingId) setReviewConfirmBooking(null); }}
          onConfirm={() => handleRequestReview(reviewConfirmBooking.id)}
          sending={reviewRequestingId === reviewConfirmBooking.id}
        />
      )}
    </div>
  );
};

export default ClientProfile;
